import React, { useMemo } from 'react';
import { StackedBarChart } from '../ui/chart-wrapper';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportChartDataToCSV } from '../../utils/exportToCSV';

/**
 * Multi-Stack Bar Widget Component
 * Modernized replacement for MultiStackBar_List.js
 * Displays stacked bar chart with multiple datasets
 */
function MultiStackBarWidget({ data }) {
    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Transform API data to Chart.js format
    const chartData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            const columnData = apiData[0].columnDataSet1;
            if (!columnData || columnData.length === 0) return null;

            // Get labels from first dataset
            const labels = columnData[0].labels;

            // Build datasets array
            const datasets = columnData.map((dataset, index) => {
                const colors = ['#f19f57', '#313131', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];
                const color = colors[index % colors.length];

                return {
                    label: dataset.label || `Series ${index + 1}`,
                    data: dataset.data,
                    backgroundColor: color,
                    borderColor: color,
                    borderWidth: 1
                };
            });

            return {
                labels,
                datasets
            };
        } catch (err) {
            console.error('Error transforming stacked bar chart data:', err);
            return null;
        }
    }, [apiData, data]);

    // Transform to table format
    const tableData = useMemo(() => {
        if (!chartData) return [];

        return chartData.labels.map((label, index) => {
            const row = { category: label, total: 0 };
            chartData.datasets.forEach((dataset, dsIndex) => {
                const value = dataset.data[index];
                row[`series${dsIndex}`] = value;
                row.total += value || 0;
            });
            return row;
        });
    }, [chartData]);

    // Table columns
    const columns = useMemo(() => {
        if (!chartData) return [];

        const cols = [
            {
                key: 'category',
                label: data.XlabelString || 'Category',
                sortable: true
            }
        ];

        chartData.datasets.forEach((dataset, index) => {
            cols.push({
                key: `series${index}`,
                label: dataset.label,
                sortable: true,
                format: (val) => typeof val === 'number' ? val.toLocaleString() : val
            });
        });

        cols.push({
            key: 'total',
            label: 'Total',
            sortable: true,
            format: (val) => typeof val === 'number' ? val.toLocaleString() : val
        });

        return cols;
    }, [chartData, data]);

    // Export handler
    const handleExport = () => {
        if (chartData) {
            exportChartDataToCSV(chartData, data.title || 'stacked-bar-chart-data');
        }
    };

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={
                chartData ? (
                    <StackedBarChart
                        datasets={chartData.datasets}
                        labels={chartData.labels}
                        height={350}
                        options={{
                            scales: {
                                x: {
                                    title: {
                                        display: true,
                                        text: data.XlabelString || ''
                                    }
                                },
                                y: {
                                    title: {
                                        display: true,
                                        text: data.YlabelString || ''
                                    },
                                    beginAtZero: true
                                }
                            },
                            plugins: {
                                tooltip: {
                                    mode: 'index',
                                    intersect: false
                                }
                            }
                        }}
                    />
                ) : null
            }
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        pagination={{ enabled: true, itemsPerPage: 10 }}
                        sortable={true}
                        searchable={true}
                    />
                ) : null
            }
            onExport={handleExport}
            showToggle={true}
            showExport={true}
        />
    );
}

export default MultiStackBarWidget;
